import React, { useState, useEffect } from 'react';
import { Box, Modal, Card, CardContent, Typography, Button, Checkbox, Alert, Stack, FormControl } from '@mui/joy';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store';
import { applicantSignUpThunk } from '../../../../redux/authSlice';
import { sendOTPThunk, verifyOTPThunk, resendOTPThunk } from '../../../../redux/applyWorkSlice';
import PhoneInput from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import FormField from "../../../../components/steps/FormField";
import Info from "../../../../assets/svgs/info.svg";

interface ContactInfoModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: ContactFormData) => void;
  companyName: string;
  positionName: string;
  employerUuid: string;
  employerPositionId: string;
}

interface ContactFormData {
  fullName: string;
  phoneNumber: string;
  zipCode: string;
  agreeToTerms: boolean;
  applicantUuid?: string;
}

const validationSchema = Yup.object({
  fullName: Yup.string()
    .required('Full name is required')
    .min(2, 'Full name must be at least 2 characters'),
  phoneNumber: Yup.string()
    .required('Phone number is required'),
  zipCode: Yup.string()
    .required('ZIP code is required')
    .matches(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code'),
  agreeToTerms: Yup.boolean()
    .oneOf([true], 'You must agree to the terms and conditions')
});

const ContactInfoModal: React.FC<ContactInfoModalProps> = ({
  open,
  onClose,
  onSubmit,
  companyName,
  positionName,
  employerUuid,
  employerPositionId
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [applicantUuid, setApplicantUuid] = useState<string | null>(null);
  const [codeDigits, setCodeDigits] = useState(Array(6).fill(""));
  const [resendTimer, setResendTimer] = useState(60);
  const [showResend, setShowResend] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  // Get existing person data for pre-population
  const getInitialValues = () => {
    const existingData = localStorage.getItem('applicantData');
    if (existingData) {
      try {
        const parsedData = JSON.parse(existingData);
        return {
          fullName: parsedData.fullName || '',
          phoneNumber: parsedData.phoneNumber || '',
          zipCode: parsedData.zipCode || '',
          agreeToTerms: parsedData.agreeToTerms || false
        };
      } catch (error) {
        console.error('Error parsing applicant data:', error);
      }
    }
    return {
      fullName: '',
      phoneNumber: '',
      zipCode: '',
      agreeToTerms: false
    };
  };

  const formik = useFormik<ContactFormData>({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      
      try {
        // Create the person signup payload
        const signupPayload = {
          name: values.fullName,
          phoneNo: values.phoneNumber,
          phoneNumber: values.phoneNumber,
          zipCode: values.zipCode,
          videoUuid: null, // No video yet, will be updated later
          resumeDocumentId: null, // No resume yet, will be updated later
          submittedToEmployerId: employerUuid,
          submittedToEmployerPositionId: employerPositionId,
        };
        
        // Call the backend to create the person
        const response = await dispatch(applicantSignUpThunk(signupPayload)).unwrap();
        
        // Store phone number and person UUID for OTP verification
        setPhoneNumber(values.phoneNumber);
        setApplicantUuid(response.data?.applicant?.uuid || null);
        
        // Send OTP to the phone number
        await dispatch(sendOTPThunk({ phoneNo: values.phoneNumber })).unwrap();
        
        // Show OTP verification screen
        setShowOTPVerification(true);
        setResendTimer(60);
        setShowResend(false);
        
      } catch (error: any) {
        console.error('Error creating applicant:', error);
        setError(error.message || 'Failed to submit application. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  });

  const handleClose = () => {
    formik.resetForm();
    setShowOTPVerification(false);
    setCodeDigits(Array(6).fill(""));
    setError(null);
    setVerificationError(null);
    setApplicantUuid(null);
    onClose();
  };
  
  // Reset to contact form screen when modal opens
  useEffect(() => {
    if (open) {
      setShowOTPVerification(false);
      setCodeDigits(Array(6).fill(""));
      setError(null);
      setVerificationError(null);
      setApplicantUuid(null);
      
      // Reset form with current localStorage data
      formik.resetForm({
        values: getInitialValues()
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  
  // OTP Timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (showOTPVerification && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setShowResend(true);
    }
    
    return () => clearInterval(timer);
  }, [showOTPVerification, resendTimer]);
  
  // OTP code input handlers
  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);
      
      if (value !== "" && index < 5) {
        const nextInput = document.getElementById(`otp-code-${index + 1}`);
        nextInput?.focus();
      }
    }
  };
  
  const handleBackspace = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && codeDigits[index] === "" && index > 0) {
      const prevInput = document.getElementById(`otp-code-${index - 1}`);
      prevInput?.focus();
    }
  };
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };
  
  const isOtpComplete = codeDigits.every((digit) => digit.length === 1);
  
  const handleVerifyOTP = async () => {
    setOtpLoading(true);
    setVerificationError(null);
    
    try {
      const otp = codeDigits.join("");
      await dispatch(verifyOTPThunk({ phoneNo: phoneNumber, otp })).unwrap();
      
      // OTP verified successfully, proceed to video application
      onSubmit({ 
        ...formik.values, 
        applicantUuid: applicantUuid || undefined
      });
      
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      setVerificationError(error.message || 'Invalid verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };
  
  const handleResendOTP = async () => {
    setOtpLoading(true);
    setVerificationError(null);
    
    try {
      await dispatch(resendOTPThunk({ phoneNo: phoneNumber })).unwrap();
      setResendTimer(60);
      setShowResend(false);
      setCodeDigits(Array(6).fill(""));
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      setVerificationError('Failed to resend verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };
  
  const formatPhoneNumber = (phone: string) => {
    if (phone.startsWith("+1") && phone.length === 12) {
      return `+1 *** **** * ${phone.slice(-2)}`;
    } else if (phone.length === 10) {
      return `(${phone.slice(0, 3)}) ***-** ${phone.slice(-2)}`;
    }
    return phone;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          bgcolor: 'white',
          borderRadius: '16px',
          p: { xs: 3, md: 4 },
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: 'none',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {!showOTPVerification ? (
            <>
              <Typography
                level="h3"
                sx={{
                  fontSize: { xs: '1.5rem', md: '1.875rem' },
                  fontWeight: 600,
                  color: '#1A1B1D',
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                {positionName}
              </Typography>
              
              <Typography
                level="body-lg"
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  color: '#7B7F85',
                  mb: 4,
                  textAlign: 'center'
                }}
              >
                at {companyName}
              </Typography>
            </>
          ) : (
            <Stack spacing={1} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
              <Typography
                level="h3"
                sx={{
                  fontSize: { xs: '1.5rem', md: '1.875rem' },
                  fontWeight: 600,
                  color: '#1A1B1D'
                }}
              >
                6-digit code
              </Typography>
              
              <Typography
                level="body-lg"
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  color: '#7B7F85'
                }}
              >
                Code sent to {formatPhoneNumber(phoneNumber)}
              </Typography>
            </Stack>
          )}

          {!showOTPVerification ? (
            <Box component="form" onSubmit={formik.handleSubmit}>
            {/* Full Name Field */}
            <FormField
              name="fullName"
              label="Full name"
              placeholder="Your name"
              value={formik.values.fullName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.fullName}
              error={formik.errors.fullName}
              autoComplete="name"
            />

            {/* Phone Number Field */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "16px",
              }}
            >
              <Box
                component="label"
                htmlFor="phoneNumber"
                sx={{
                  fontSize: {
                    xs: "16px",
                    lg: "18px",
                  },
                  fontWeight: 500,
                  color: "#1A1B1D",
                  mb: {
                    xs: "8px",
                    lg: "12px",
                  },
                }}
              >
                Phone number
              </Box>
              <PhoneInput
                defaultCountry="US"
                value={formik.values.phoneNumber}
                placeholder="Your phone number"
                onChange={(value) => formik.setFieldValue("phoneNumber", value)}
                onBlur={() => formik.setFieldTouched("phoneNumber", true)}
                name="phoneNumber"
                id="phoneNumber"
                style={{
                  height: "52px",
                  width: "100%",
                  borderRadius: "12px",
                  backgroundColor: "#edeff3",
                  border: "none",
                  outline: "none",
                  padding: "0 12px",
                  fontSize: "16px",
                  fontFamily: "inherit",
                }}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <Box
                  sx={{
                    color: "#ef4444",
                    fontSize: "14px",
                    mt: "4px",
                  }}
                >
                  {formik.errors.phoneNumber}
                </Box>
              )}
            </Box>

            {/* ZIP Code Field */}
            <FormField
              name="zipCode"
              label="Zip code"
              placeholder="Your zip code"
              value={formik.values.zipCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={formik.touched.zipCode}
              error={formik.errors.zipCode}
              autoComplete="postal-code"
              infoText="Enter your ZIP code to get matched with local employers near you"
              infoIcon={Info}
              type="tel"
            />

            {/* Terms and Conditions Checkbox */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 3,
                mt: 2
              }}
            >
              <Checkbox
                checked={formik.values.agreeToTerms}
                onChange={(event) => formik.setFieldValue('agreeToTerms', event.target.checked)}
                onBlur={() => formik.setFieldTouched('agreeToTerms', true)}
                sx={{
                  mt: 0.25,
                  '& .MuiCheckbox-checkbox': {
                    borderRadius: '4px'
                  },
                  '& .MuiCheckbox-checkbox.Mui-checked': {
                    borderRadius: '4px',
                    backgroundColor: '#2E2D7E'
                  }
                }}
              />
              <Typography
                level="body-sm"
                sx={{
                  fontSize: '14px',
                  color: '#1A1B1D',
                  lineHeight: 1.4
                }}
              >
                I agree to the{' '}
                <Typography
                  component="a"
                  href="/terms"
                  target="_blank"
                  sx={{
                    color: '#2E2D7E',
                    textDecoration: 'underline',
                    '&:hover': {
                      textDecoration: 'none'
                    }
                  }}
                >
                  Terms of Service
                </Typography>{' '}
                and{' '}
                <Typography
                  component="a"
                  href="/privacy"
                  target="_blank"
                  sx={{
                    color: '#2E2D7E',
                    textDecoration: 'underline',
                    '&:hover': {
                      textDecoration: 'none'
                    }
                  }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            </Box>
            {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
              <Box
                sx={{
                  color: "#ef4444",
                  fontSize: "14px",
                  mt: "-12px",
                  mb: "16px"
                }}
              >
                {formik.errors.agreeToTerms}
              </Box>
            )}

            {/* Submit Button */}
            {/* Error Alert */}
            {error && (
              <Alert
                color="danger"
                sx={{
                  mb: 2,
                  fontSize: '14px'
                }}
              >
                {error}
              </Alert>
            )}
            
            <Button
              type="submit"
              variant="solid"
              size="lg"
              loading={loading}
              disabled={!formik.isValid || !formik.dirty || loading}
              sx={{
                bgcolor: '#2E2D7E',
                color: 'white',
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 600,
                borderRadius: '12px',
                width: '100%',
                mb: 2,
                '&:hover': {
                  bgcolor: '#1a1a5e',
                },
                '&:disabled': {
                  bgcolor: '#E5E7EB',
                  color: '#9CA3AF'
                }
              }}
            >
              {loading ? 'Submitting...' : 'NEXT'}
            </Button>            
          </Box>
          ) : (
            /* OTP Verification Screen */
            <Stack spacing={4} alignItems="center">
              {/* OTP Input Grid */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "nowrap",
                  gap: 1.5,
                  px: 2,
                }}
              >
                {codeDigits.map((digit, index) => (
                  <React.Fragment key={index}>
                    {index === 3 && (
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          fontWeight: 400,
                          color: "#7B7F85",
                        }}
                      >
                        -
                      </Typography>
                    )}
                    <FormControl
                      sx={{
                        width: 44,
                        height: 52,
                        "& input": {
                          padding: 0,
                          textAlign: "center",
                          fontSize: "1.5rem",
                          backgroundColor: "#ECEFF3",
                          borderRadius: "16px",
                          width: "100%",
                          height: "100%",
                          outline: "none",
                          border: "none",
                        },
                      }}
                    >
                      <input
                        id={`otp-code-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit ? "•" : ""}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleBackspace(index, e)}
                        autoComplete="one-time-code"
                      />
                    </FormControl>
                  </React.Fragment>
                ))}
              </Box>
              
              {/* Resend Timer/Button */}
              <Stack spacing={1} alignItems="center" textAlign="center">
                {showResend ? (
                  <Typography
                    level="body-sm"
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "#2e2d7e",
                      textDecoration: !otpLoading ? "underline" : "none",
                    }}
                    onClick={handleResendOTP}
                  >
                    {otpLoading ? "Sending..." : "Resend verification code"}
                  </Typography>
                ) : (
                  <Typography
                    level="body-sm"
                    sx={{
                      fontSize: "14px",
                      color: "#7B7F85"
                    }}
                  >
                    Resend code in {formatTime(resendTimer)}
                  </Typography>
                )}
              </Stack>
              
              {/* Error Alert for OTP */}
              {verificationError && (
                <Alert
                  color="danger"
                  sx={{
                    width: '100%',
                    fontSize: '14px'
                  }}
                >
                  {verificationError}
                </Alert>
              )}
              
              {/* Verify Button */}
              <Button
                variant="solid"
                size="lg"
                loading={otpLoading}
                disabled={!isOtpComplete || otpLoading}
                onClick={handleVerifyOTP}
                sx={{
                  bgcolor: '#2E2D7E',
                  color: 'white',
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  width: '100%',
                  '&:hover': {
                    bgcolor: '#1a1a5e',
                  },
                  '&:disabled': {
                    bgcolor: '#E5E7EB',
                    color: '#9CA3AF'
                  }
                }}
              >
                {otpLoading ? 'Verifying...' : 'VERIFY'}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Modal>
  );
};

export default ContactInfoModal;
